import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';

const secondaryBtnClass =
  'shrink-0 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50';

const primaryBtnClass =
  'rounded-lg bg-brand-olive px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95 active:opacity-90';

/** Decorative only — matches disabled primary look; must stay pointer-events-none (never hover/click/focus). */
const phantomBtnClass = `${primaryBtnClass} pointer-events-none select-none opacity-[0.58] saturate-[0.65] shadow-none`;

export const TEAM_SUBMIT_TOOLTIP = 'Complete all fields first 😄';

const RING_CLASSES = [
  'ring-2',
  'ring-amber-400',
  'ring-offset-1',
  'ring-offset-white',
  'transition-[box-shadow]',
  'duration-200',
];

const NEAR_PHANTOM_PX = 72;
const NEAR_FEEDBACK_COOLDOWN_MS = 820;

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function shakeElement(el) {
  if (!el) return;
  const tl = gsap.timeline();
  tl.to(el, { x: -10, duration: 0.06, ease: 'power1.inOut' })
    .to(el, { x: 10, duration: 0.06, ease: 'power1.inOut' })
    .to(el, { x: -8, duration: 0.06, ease: 'power1.inOut' })
    .to(el, { x: 8, duration: 0.06, ease: 'power1.inOut' })
    .to(el, { x: -4, duration: 0.06, ease: 'power1.inOut' })
    .to(el, { x: 0, duration: 0.1, ease: 'power2.out' });
}

function ringField(el) {
  if (!el) return;
  el.classList.add(...RING_CLASSES);
  window.setTimeout(() => {
    el.classList.remove(...RING_CLASSES);
  }, 1400);
}

/**
 * Add Team Member: decorative fleeing “ghost” of the submit control until valid — never focusable or clickable.
 */
export default function TeamMemberModalFooter({
  onCancel,
  onApply,
  applyLabel = 'Add person',
  fieldsValid,
  dodgeEnabled,
  draftSnapshot,
  nameFieldRef,
  roleFieldRef,
  bioFieldRef,
  imageFieldRef,
  reducedMotion,
  fleeBoundsRef,
}) {
  const submitSlotRef = useRef(null);
  const phantomRef = useRef(null);
  const rafRef = useRef(0);
  const prevFieldsValidRef = useRef(fieldsValid);
  const nearFeedbackAtRef = useRef(0);
  const [showTip, setShowTip] = useState(false);
  const [exitSnap, setExitSnap] = useState(false);

  const showPhantom =
    dodgeEnabled && !reducedMotion && (!fieldsValid || exitSnap);
  const fleePointerActive = dodgeEnabled && !fieldsValid && !reducedMotion;

  const showInvalidFeedback = useCallback(() => {
    setShowTip(true);
    const snap = draftSnapshot || {};
    const rows = [
      { ref: nameFieldRef, empty: !String(snap.name ?? '').trim() },
      { ref: roleFieldRef, empty: !String(snap.role ?? '').trim() },
      { ref: bioFieldRef, empty: !String(snap.bio ?? '').trim() },
      { ref: imageFieldRef, empty: !String(snap.image ?? '').trim() },
    ];
    let delay = 0;
    let focused = false;
    for (const { ref, empty } of rows) {
      const el = ref?.current;
      if (!empty || !el) continue;
      window.setTimeout(() => {
        if (!reducedMotion) shakeElement(el);
        ringField(el);
        if (!focused) {
          el.focus({ preventScroll: true });
          focused = true;
        }
      }, delay);
      delay += 115;
    }
  }, [draftSnapshot, nameFieldRef, roleFieldRef, bioFieldRef, reducedMotion]);

  const getHomeCoords = useCallback(() => {
    const slot = submitSlotRef.current;
    const ph = phantomRef.current;
    if (!slot || !ph) return null;
    const sr = slot.getBoundingClientRect();
    const w = ph.offsetWidth || 120;
    const h = ph.offsetHeight || 40;
    const left = clamp(sr.right - w, sr.left + 8, sr.right - w - 8);
    const top = clamp(sr.top + (sr.height - h) / 2, sr.top + 4, sr.bottom - h - 4);
    return { left, top };
  }, []);

  const placePhantomHome = useCallback(() => {
    const ph = phantomRef.current;
    const coords = getHomeCoords();
    if (!ph || !coords) return;
    gsap.killTweensOf(ph);
    gsap.set(ph, {
      position: 'fixed',
      left: coords.left,
      top: coords.top,
      zIndex: 210,
      opacity: 1,
      x: 0,
      y: 0,
    });
  }, [getHomeCoords]);

  const fleeFromCursor = useCallback(
    (clientX, clientY) => {
      const panel = fleeBoundsRef?.current;
      const ph = phantomRef.current;
      if (!panel || !ph || reducedMotion || !fleePointerActive) return;
      const pr = panel.getBoundingClientRect();
      const br = ph.getBoundingClientRect();
      const w = br.width;
      const h = br.height;
      const cx = clientX;
      const cy = clientY;
      const bx = br.left + w / 2;
      const by = br.top + h / 2;
      const reachDist = Math.hypot(cx - bx, cy - by);
      if (reachDist < NEAR_PHANTOM_PX) {
        const now = Date.now();
        if (now - nearFeedbackAtRef.current > NEAR_FEEDBACK_COOLDOWN_MS) {
          nearFeedbackAtRef.current = now;
          showInvalidFeedback();
        }
      }

      const dx = bx - cx;
      const dy = by - cy;
      const dist = Math.hypot(dx, dy);
      const avoidRadius = 104;
      if (dist > avoidRadius || dist < 0.5) return;
      const push = ((avoidRadius - dist) / avoidRadius) * 46;
      const nx = bx + (dx / dist) * push;
      const ny = by + (dy / dist) * push;
      let left = nx - w / 2;
      let top = ny - h / 2;
      left = clamp(left, pr.left + 10, pr.right - w - 10);
      top = clamp(top, pr.top + 10, pr.bottom - h - 10);
      gsap.to(ph, {
        left,
        top,
        duration: 0.42,
        ease: 'power3.out',
        overwrite: 'auto',
      });
    },
    [fleeBoundsRef, reducedMotion, fleePointerActive, showInvalidFeedback]
  );

  useLayoutEffect(() => {
    const prev = prevFieldsValidRef.current;
    prevFieldsValidRef.current = fieldsValid;
    if (!dodgeEnabled || reducedMotion) {
      setExitSnap(false);
      return;
    }
    if (fieldsValid && !prev) {
      setExitSnap(true);
    }
    if (!fieldsValid) {
      setExitSnap(false);
    }
  }, [fieldsValid, dodgeEnabled, reducedMotion]);

  useLayoutEffect(() => {
    if (!showPhantom) return undefined;
    if (exitSnap && fieldsValid) return undefined;
    let id1 = 0;
    let id2 = 0;
    id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => placePhantomHome());
    });
    return () => {
      cancelAnimationFrame(id1);
      cancelAnimationFrame(id2);
    };
  }, [showPhantom, exitSnap, fieldsValid, placePhantomHome, applyLabel]);

  useEffect(() => {
    if (!exitSnap || !fieldsValid || !dodgeEnabled || reducedMotion) return undefined;
    let cancelled = false;
    const runId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const ph = phantomRef.current;
        if (!ph) {
          setExitSnap(false);
          return;
        }
        const coords = getHomeCoords();
        if (!coords) {
          setExitSnap(false);
          return;
        }
        gsap.killTweensOf(ph);
        gsap.to(ph, {
          left: coords.left,
          top: coords.top,
          duration: 0.32,
          ease: 'power3.out',
          onComplete: () => {
            if (!cancelled) setExitSnap(false);
          },
        });
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(runId);
    };
  }, [exitSnap, fieldsValid, dodgeEnabled, reducedMotion, getHomeCoords]);

  useEffect(() => {
    if (!fleePointerActive) return undefined;
    const panel = fleeBoundsRef?.current;
    if (!panel) return undefined;

    const onMove = (e) => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => fleeFromCursor(e.clientX, e.clientY));
    };

    panel.addEventListener('pointermove', onMove, { passive: true, capture: true });
    return () => {
      panel.removeEventListener('pointermove', onMove, true);
      cancelAnimationFrame(rafRef.current);
    };
  }, [fleePointerActive, fleeFromCursor, fleeBoundsRef]);

  useEffect(() => {
    if (!showPhantom) return undefined;
    const onResize = () => {
      if (fleePointerActive) placePhantomHome();
      else if (exitSnap && fieldsValid) {
        const c = getHomeCoords();
        const ph = phantomRef.current;
        if (c && ph) gsap.set(ph, { left: c.left, top: c.top });
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [showPhantom, fleePointerActive, exitSnap, fieldsValid, placePhantomHome, getHomeCoords]);

  useEffect(() => {
    if (fieldsValid) setShowTip(false);
  }, [fieldsValid]);

  useEffect(() => {
    if (!showTip) return undefined;
    const t = window.setTimeout(() => setShowTip(false), 3600);
    return () => window.clearTimeout(t);
  }, [showTip]);

  useEffect(
    () => () => {
      [nameFieldRef, roleFieldRef, bioFieldRef, imageFieldRef].forEach((r) => {
        const el = r?.current;
        if (el) gsap.killTweensOf(el);
      });
      if (phantomRef.current) gsap.killTweensOf(phantomRef.current);
    },
    [nameFieldRef, roleFieldRef, bioFieldRef, imageFieldRef]
  );

  const onRealSubmit = () => {
    if (!fieldsValid) {
      showInvalidFeedback();
      return;
    }
    onApply();
  };

  const reducedMotionInvalidClass =
    !fieldsValid && dodgeEnabled && reducedMotion
      ? `${primaryBtnClass} whitespace-nowrap opacity-50 cursor-not-allowed saturate-[0.7]`
      : `${primaryBtnClass} whitespace-nowrap`;

  return (
    <div className="flex w-full flex-wrap items-center gap-2">
      <button type="button" onClick={onCancel} className={secondaryBtnClass}>
        Cancel
      </button>

      <div
        ref={submitSlotRef}
        className="relative ml-auto flex min-h-[2.75rem] min-w-[200px] flex-1 items-center justify-end sm:min-h-[3rem] sm:max-w-lg"
      >
        {showTip && !fieldsValid ? (
          <span
            className="pointer-events-none absolute -top-11 right-0 z-10 max-w-[18rem] rounded-xl bg-brand-forest px-3 py-1.5 text-center text-xs font-medium leading-snug text-white shadow-lg ring-1 ring-black/10"
            role="status"
            aria-live="polite"
          >
            {TEAM_SUBMIT_TOOLTIP}
          </span>
        ) : null}

        {showPhantom ? (
          <span
            ref={phantomRef}
            className={`${phantomBtnClass} fixed z-[210] inline-flex whitespace-nowrap will-change-[left,top]`}
            style={{ left: -9999, top: 0, opacity: 0 }}
            aria-hidden="true"
          >
            {applyLabel}
          </span>
        ) : (
          <button
            type="button"
            onClick={onRealSubmit}
            disabled={dodgeEnabled && reducedMotion && !fieldsValid}
            className={
              dodgeEnabled && reducedMotion && !fieldsValid
                ? reducedMotionInvalidClass
                : `${primaryBtnClass} whitespace-nowrap`
            }
            title={!fieldsValid ? TEAM_SUBMIT_TOOLTIP : undefined}
            aria-disabled={!fieldsValid}
          >
            {applyLabel}
          </button>
        )}
      </div>
    </div>
  );
}
